/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Organizations Hooks - React Query for Organizations
 * ============================================================================
 *
 * Custom hooks for organization data fetching and mutations using React Query.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organizationsService } from '@/services/superadmin/organizations.service';
import {
  OrganizationListItem,
  OrganizationDetail,
  OrganizationAdmin,
  OrgRoleItem,
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationFilterParams,
  CreateOrgUserRequest,
  UpdateOrgUserRequest,
  GenerateTestDataRequest,
  GenerateTestDataResponse,
  PremiumTemplate,
  AssignedPremiumTemplate,
} from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';

// Query keys for cache management
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (params?: PaginationParams & OrganizationFilterParams) =>
    [...organizationKeys.lists(), params] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  assignableTemplates: () => [...organizationKeys.all, 'assignableTemplates'] as const,
  assignedTemplates: (orgId: string) => [...organizationKeys.all, 'assignedTemplates', orgId] as const,
};

/**
 * Hook to fetch paginated organizations list
 */
export function useOrganizations(params?: PaginationParams & OrganizationFilterParams) {
  return useQuery<PaginatedResponse<OrganizationListItem>, ApiError>({
    queryKey: organizationKeys.list(params),
    queryFn: () => organizationsService.getOrganizations(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single organization detail
 */
export function useOrganization(id: string) {
  return useQuery<OrganizationDetail, ApiError>({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationsService.getOrganization(id),
    enabled: !!id,
  });
}

/**
 * Hook for creating organization
 */
export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation<Organization, ApiError, CreateOrganizationRequest>({
    mutationFn: organizationsService.createOrganization,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for updating organization
 */
export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation<Organization, ApiError, { id: string; data: UpdateOrganizationRequest }>({
    mutationFn: ({ id, data }) => organizationsService.updateOrganization(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for suspending organization
 */
export function useSuspendOrganization() {
  const queryClient = useQueryClient();

  return useMutation<Organization, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => organizationsService.suspendOrganization(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for reactivating organization
 */
export function useReactivateOrganization() {
  const queryClient = useQueryClient();

  return useMutation<Organization, ApiError, string>({
    mutationFn: organizationsService.reactivateOrganization,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook for cancelling organization
 */
export function useCancelOrganization() {
  const queryClient = useQueryClient();

  return useMutation<Organization, ApiError, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) => organizationsService.cancelOrganization(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Hook to fetch available RBAC roles for an organization (for Create User dropdown)
 */
export function useOrgRoles(orgId: string | undefined) {
  return useQuery<OrgRoleItem[], ApiError>({
    queryKey: [...organizationKeys.detail(orgId ?? ''), 'roles'],
    queryFn: () => organizationsService.getOrgRoles(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for creating a user within an organization
 */
export function useCreateOrgUser() {
  const queryClient = useQueryClient();

  return useMutation<OrganizationAdmin, ApiError, { orgId: string; data: CreateOrgUserRequest }>({
    mutationFn: ({ orgId, data }) => organizationsService.createOrgUser(orgId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
    },
  });
}

/**
 * Hook for updating a user within an organization
 */
export function useUpdateOrgUser() {
  const queryClient = useQueryClient();

  return useMutation<OrganizationAdmin, ApiError, { orgId: string; userId: string; data: UpdateOrgUserRequest }>({
    mutationFn: ({ orgId, userId, data }) => organizationsService.updateOrgUser(orgId, userId, data),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
    },
  });
}

/**
 * Hook for resending invitation email to an organization user
 */
export function useResendOrgUserInvite() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { orgId: string; userId: string }>({
    mutationFn: ({ orgId, userId }) => organizationsService.resendOrgUserInvite(orgId, userId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
    },
  });
}

/**
 * Hook for manually provisioning a cash register for an org user
 */
export function useProvisionRegister() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { orgId: string; userId: string }>({
    mutationFn: ({ orgId, userId }) => organizationsService.provisionRegister(orgId, userId),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(orgId) });
    },
  });
}

/**
 * Hook for generating random test data (organizations and partners)
 */
export function useGenerateTestData() {
  const queryClient = useQueryClient();

  return useMutation<GenerateTestDataResponse, ApiError, GenerateTestDataRequest>({
    mutationFn: organizationsService.generateTestData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

// ============================================================================
// Premium Invoice Template Hooks
// ============================================================================

/**
 * Hook to fetch premium templates available for assignment
 */
export function useAssignableTemplates() {
  return useQuery<PremiumTemplate[], ApiError>({
    queryKey: organizationKeys.assignableTemplates(),
    queryFn: () => organizationsService.getAssignableTemplates(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch premium templates assigned to an organization
 */
export function useAssignedTemplates(orgId: string) {
  return useQuery<AssignedPremiumTemplate[], ApiError>({
    queryKey: organizationKeys.assignedTemplates(orgId),
    queryFn: () => organizationsService.getAssignedTemplates(orgId),
    enabled: !!orgId,
  });
}

/**
 * Hook for assigning a premium template to an organization
 */
export function useAssignTemplate() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { orgId: string; code: string }>({
    mutationFn: ({ orgId, code }) => organizationsService.assignTemplate(orgId, code),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.assignedTemplates(orgId) });
    },
  });
}

/**
 * Hook for unassigning a premium template from an organization
 */
export function useUnassignTemplate() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, { orgId: string; code: string }>({
    mutationFn: ({ orgId, code }) => organizationsService.unassignTemplate(orgId, code),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.assignedTemplates(orgId) });
    },
  });
}
